import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FiChevronRight } from 'react-icons/fi';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { MusicRecords } from '../../pages/mypage/components/MyMusicRecords';
import { unwrapRecords, recordPath } from '../../pages/mypage/components/myRecordsModel';
import usePublicRecords from './usePublicRecords';
import MyTaste from '../taste/MyTaste';
import { ActivityRecords, BadgeEntry } from '../badges/MyActivity';
import useBadgeStyle from '../badges/useBadgeStyle';
import { BadgeSkeleton } from '../badges/States';
import MeasuredContentTitle from '../../components/content/MeasuredContentTitle';
import ContentShelf, { ShelfNavigationContext } from '../../components/responsive/ContentShelf';
import { profileConnections, profileWords } from './profileConnectionModel';
import './PublicProfileSummary.css';
function Art({ item }) {
  return (
    <span className="pp-profile-connection-art">
      postpop
      {item.album.imageUrl && (
        <img
          src={item.album.imageUrl}
          alt=""
          loading="lazy"
          onError={(e) => {
            e.currentTarget.style.visibility = 'hidden';
          }}
        />
      )}
    </span>
  );
}
function AlbumRail({ label, items }) {
  const { locale } = useWebLocale();
  return (
    <ShelfNavigationContext.Provider value="edges">
      <ContentShelf label={label} className="pp-profile-connection-rail">
        {items.map((item) => (
          <Link key={item.review.id} to={recordPath(item, 'ratings')}>
            <Art item={item} />
            <MeasuredContentTitle
              title={item.album.title}
              explicit={item.album.contentRating === 'EXPLICIT'}
            />
            <span className="pp-profile-connection-artist">
              {item.artist?.name ?? (locale === 'en' ? 'Unknown artist' : '아티스트 정보 없음')}
            </span>
          </Link>
        ))}
      </ContentShelf>
    </ShelfNavigationContext.Provider>
  );
}
export function ProfileConnections({ profile, records }) {
  const viewer = useUserStore((s) => s.user),
    { locale } = useWebLocale(),
    t = profileWords(locale),
    style = useBadgeStyle();
  const enabled = !!viewer?.id && String(viewer.id) !== String(profile.id);
  const q = useQuery({
    queryKey: ['myMusicRecords', viewer?.id, 'albums'],
    queryFn: async ({ signal }) =>
      unwrapRecords(await api.getAuth('/me/reviews', { signal, suppressGlobalError: true })),
    enabled,
    retry: false,
    gcTime: 0,
  });
  if (!enabled) return null;
  const error = q.isError || records.queries.albums.isError,
    pending = q.isPending || records.queries.albums.isPending;
  const { shared, discoveries, divergent } = profileConnections(records.data.albums, q.data);
  return (
    <section
      className="pp-badges pp-profile-connections"
      style={style}
      aria-label={t('profile.sharedAlbums')}
    >
      <h2>{t('profile.sharedAlbums')}</h2>
      {error ? (
        <div role="alert">
          <p>
            {locale === 'en'
              ? 'Could not compare album ratings.'
              : '앨범 평가를 비교하지 못했어요.'}
          </p>
          <button
            onClick={() => {
              void q.refetch();
              void records.queries.albums.refetch();
            }}
          >
            {locale === 'en' ? 'Retry' : '다시 시도'}
          </button>
        </div>
      ) : pending ? (
        <BadgeSkeleton rows={2} />
      ) : (
        <div className="pp-profile-connection-stack">
          {shared.length ? (
            <AlbumRail label={t('profile.sharedAlbums')} items={shared} />
          ) : (
            <p className="pp-profile-shared-empty">{t('connection.noShared')}</p>
          )}
          {discoveries.length > 0 && (
            <div className="pp-profile-connection-group">
              <header>
                <h2>{t('connection.unratedByMe')}</h2>
                <p>{t('connection.targetHighRating', { nickname: profile.nickname })}</p>
              </header>
              <AlbumRail label={t('connection.unratedByMe')} items={discoveries} />
            </div>
          )}
          {divergent.length > 0 && (
            <div className="pp-profile-connection-divergent">
              <h2>{t('connection.dividedRatings')}</h2>
              {divergent.map(({ item, mine }) => (
                <Link key={item.review.id} to={recordPath(item, 'ratings')}>
                  <Art item={item} />
                  <span>
                    <MeasuredContentTitle
                      title={item.album.title}
                      explicit={item.album.contentRating === 'EXPLICIT'}
                    />
                    <small>
                      {t('connection.ratingComparison', {
                        mine: Number(mine).toFixed(1),
                        theirs: Number(item.review.rating).toFixed(1),
                      })}
                    </small>
                  </span>
                  <FiChevronRight size={17} />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
export default function PublicProfileSummary({ profile }) {
  const records = usePublicRecords(profile),
    { locale } = useWebLocale(),
    t = profileWords(locale),
    base = `/users/${encodeURIComponent(profile.nickname)}`;
  return (
    <div className="pp-public-profile-summary">
      <MusicRecords profile={profile} records={records} basePath={base} overview />
      <MyTaste profile={profile} publicProfile />
      <ProfileConnections profile={profile} records={records} />
      <ActivityRecords
        records={records}
        title={t('profile.memberRecentActivity', { nickname: profile.nickname })}
        to={`${base}/ratings`}
      />
      <BadgeEntry profile={profile} publicProfile />
    </div>
  );
}
